import SearchAPI from '../../api/search';
import types from '../mutation-types';

// Tracks the in-flight search so a new search (or a cleared input) aborts the
// previous request wave instead of letting stale requests run to completion.
let searchAbortController = null;

// A search's commits only apply while it is still the active search: a
// superseded (aborted) or cleared search must not overwrite newer results or
// flip the loading flags. Calls without a signal (no cancellation context) are
// always treated as active.
const isActiveSearch = signal =>
  !signal ||
  (searchAbortController !== null && searchAbortController.signal === signal);

export const initialState = {
  records: [],
  contactRecords: [],
  conversationRecords: [],
  messageRecords: [],
  uiFlags: {
    isFetching: false,
    isSearchCompleted: false,
    contact: { isFetching: false },
    conversation: { isFetching: false },
    message: { isFetching: false },
  },
};

export const getters = {
  getConversations(state) {
    return state.records;
  },
  getContactRecords(state) {
    return state.contactRecords;
  },
  getConversationRecords(state) {
    return state.conversationRecords;
  },
  getMessageRecords(state) {
    return state.messageRecords;
  },
  getUIFlags(state) {
    return state.uiFlags;
  },
};

export const actions = {
  async get({ commit }, { q }) {
    commit(types.SEARCH_CONVERSATIONS_SET, []);
    if (!q) {
      return;
    }
    commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload },
      } = await SearchAPI.get({ q });
      commit(types.SEARCH_CONVERSATIONS_SET, payload);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  async fullSearch({ commit, dispatch }, { q, skipContacts = false }) {
    if (!q) {
      return;
    }
    // Abort any previous in-flight search so rapid typing does not stack
    // uncancelled request waves against the DB-heavy /search endpoints.
    if (searchAbortController) {
      searchAbortController.abort();
    }
    searchAbortController = new AbortController();
    const { signal } = searchAbortController;
    commit(types.FULL_SEARCH_SET_UI_FLAG, {
      isFetching: true,
      isSearchCompleted: false,
    });
    try {
      const searches = [
        dispatch('conversationSearch', { q, signal }),
        dispatch('messageSearch', { q, signal }),
      ];
      if (!skipContacts) {
        searches.push(dispatch('contactSearch', { q, signal }));
      } else {
        // Skipping the dispatch also skips its own CONTACT_SEARCH_SET reset, so
        // clear here to avoid leaving stale contacts from a previous search.
        commit(types.CONTACT_SEARCH_SET, []);
      }
      await Promise.all(searches);
    } catch (error) {
      // Ignore error
    } finally {
      // Only the current search flips the completion flags. A superseded search
      // leaves them to its successor; a cleared search resets them itself.
      if (isActiveSearch(signal)) {
        commit(types.FULL_SEARCH_SET_UI_FLAG, {
          isFetching: false,
          isSearchCompleted: true,
        });
      }
    }
  },
  async contactSearch({ commit }, { q, signal }) {
    commit(types.CONTACT_SEARCH_SET, []);
    commit(types.CONTACT_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.contacts({ q, signal });
      if (isActiveSearch(signal)) {
        commit(types.CONTACT_SEARCH_SET, data.payload.contacts);
      }
    } catch (error) {
      // Ignore error
    } finally {
      if (isActiveSearch(signal)) {
        commit(types.CONTACT_SEARCH_SET_UI_FLAG, { isFetching: false });
      }
    }
  },
  async conversationSearch({ commit }, { q, signal }) {
    commit(types.CONVERSATION_SEARCH_SET, []);
    commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.conversations({ q, signal });
      if (isActiveSearch(signal)) {
        commit(types.CONVERSATION_SEARCH_SET, data.payload.conversations);
      }
    } catch (error) {
      // Ignore error
    } finally {
      if (isActiveSearch(signal)) {
        commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, { isFetching: false });
      }
    }
  },
  async messageSearch({ commit }, { q, signal }) {
    commit(types.MESSAGE_SEARCH_SET, []);
    commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.messages({ q, signal });
      if (isActiveSearch(signal)) {
        commit(types.MESSAGE_SEARCH_SET, data.payload.messages);
      }
    } catch (error) {
      // Ignore error
    } finally {
      if (isActiveSearch(signal)) {
        commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: false });
      }
    }
  },
  async clearSearchResults({ commit }) {
    if (searchAbortController) {
      searchAbortController.abort();
      searchAbortController = null;
    }
    // Reset every loading flag here: the aborted in-flight wave skips its own
    // resets (it is no longer the active search), so clearing must do it.
    commit(types.FULL_SEARCH_SET_UI_FLAG, {
      isFetching: false,
      isSearchCompleted: false,
    });
    commit(types.CONTACT_SEARCH_SET_UI_FLAG, { isFetching: false });
    commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, { isFetching: false });
    commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: false });
    commit(types.MESSAGE_SEARCH_SET, []);
    commit(types.CONVERSATION_SEARCH_SET, []);
    commit(types.CONTACT_SEARCH_SET, []);
  },
};

export const mutations = {
  [types.SEARCH_CONVERSATIONS_SET](state, records) {
    state.records = records;
  },
  [types.CONTACT_SEARCH_SET](state, records) {
    state.contactRecords = records;
  },
  [types.CONVERSATION_SEARCH_SET](state, records) {
    state.conversationRecords = records;
  },
  [types.MESSAGE_SEARCH_SET](state, records) {
    state.messageRecords = records;
  },
  [types.SEARCH_CONVERSATIONS_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.FULL_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.CONTACT_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.contact = { ...state.uiFlags.contact, ...uiFlags };
  },
  [types.CONVERSATION_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.conversation = { ...state.uiFlags.conversation, ...uiFlags };
  },
  [types.MESSAGE_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.message = { ...state.uiFlags.message, ...uiFlags };
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
